/*
 * LEA School Locator — front-end controller.
 *
 * Lifecycle:
 *   DOMContentLoaded       → init() per [data-locator]
 *   User types (3+ chars)  → custom postcode autocomplete fetches suggestions
 *   User picks suggestion  → lat/lng set, Google Maps lazy-loaded if needed
 *   Maps ready             → initMap() + runSearch()
 *   Filter changed         → runSearch()
 *   Card or marker clicked → openModal()
 *
 * Autocomplete reads from the theme's custom wp_lea_au_postcodes table — we
 * never expose the Google Places UI. The Maps JS API is still used for
 * rendering the map and drawing markers, so it loads lazily on first select.
 */

(function () {
    'use strict';

    let mapsScriptInjected = false;
    let mapsReadyPromise = null;

    document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('[data-locator]').forEach(initLocator);
    });

    // ═══════════════════════════════════════════════════════════════════════
    // Per-instance controller
    // ═══════════════════════════════════════════════════════════════════════
    function initLocator(root) {
        const cfgEl = root.querySelector('[data-locator-config]');
        if (!cfgEl) return;

        let config;
        try { config = JSON.parse(cfgEl.textContent); } catch (e) { return; }

        const state = {
            lat: null, lng: null,
            suburb: '', postcode: '', stateAbbr: '',
            radius: readRadiusFromDropdown(root) || (config.defaultRadius || 100),
            regions: collectCheckedFromContainer(root.querySelector('[data-locator-dropdown="regions"]')),
            types:   collectCheckedFromContainer(root.querySelector('[data-locator-dropdown="types"]')),
            map: null,
            markers: [],
            userMarker: null,
            markersByCardId: {},
            searchPending: false,
            markerMap: config.markers || { default: config.markerUrl },
        };

        const els = {
            input:       root.querySelector('[data-locator-input]'),
            inputSuggest: root.querySelector('[data-locator-suggestions]'),
            form:        root.querySelector('[data-locator-form]'),
            mapEl:       root.querySelector('[data-locator-map]'),
            list:        root.querySelector('[data-locator-list]'),
            scroll:      root.querySelector('[data-locator-scroll]'),
            items:       root.querySelector('[data-locator-items]'),
            count:       root.querySelector('[data-locator-count]'),
            modal:       root.querySelector('[data-locator-modal]'),
            modalTitle:  root.querySelector('[data-locator-modal-title]'),
            modalPills:  root.querySelector('[data-locator-modal-pills]'),
            modalAddr:   root.querySelector('[data-locator-modal-address]'),
            modalPhone:  root.querySelector('[data-locator-modal-phone]'),
            modalPhoneWrap:   root.querySelector('[data-locator-modal-phone-wrap]'),
            modalEmail:  root.querySelector('[data-locator-modal-email]'),
            modalEmailWrap:   root.querySelector('[data-locator-modal-email-wrap]'),
            modalSite:   root.querySelector('[data-locator-modal-website]'),
            modalSiteWrap:    root.querySelector('[data-locator-modal-website-wrap]'),
            modalDirs:   root.querySelector('[data-locator-modal-directions]'),
        };

        // ─── Kick off the map immediately so Australia shows by default ───
        if (config.apiKey) {
            loadGoogleMaps(config)
                .then(() => initMap(state, els, config))
                .catch(() => {
                    showMapUnavailable(els.mapEl, config.i18n.mapUnavailable);
                    reportMapError(config, 'LOAD_FAILURE', 'Google Maps script failed to load.');
                });
        } else {
            showMapUnavailable(els.mapEl, config.i18n.mapUnavailable);
        }

        // ─── Custom postcode autocomplete ─────────────────────────────────
        if (els.input && els.inputSuggest) {
            // On touch devices, re-focusing the input after a prior selection
            // should treat it as "starting a new search" — clear the value so
            // the user doesn't have to manually delete the pre-filled text.
            // Existing map markers + results stay visible until they pick
            // something new.
            els.input.addEventListener('focus', () => {
                if (els.input.dataset.selected === '1' && els.input.value) {
                    els.input.value = '';
                    els.input.dataset.selected = '';
                }
            });

            new PostcodeAutocomplete(els.input, els.inputSuggest, config, (row) => {
                state.lat       = row.latitude;
                state.lng       = row.longitude;
                state.suburb    = row.locality;
                state.postcode  = row.postcode;
                state.stateAbbr = row.state;
                // Flag picked up by runSearch to emit a single log row per
                // fresh locality choice — filter tweaks don't re-log.
                state.newSelection = true;

                // Unlock the filter controls now that we have a location.
                // Pre-selection they're disabled with a one-line hint so users
                // don't try to filter into an empty result set.
                const filters = root.querySelector('[data-locator-filters]');
                const hint    = root.querySelector('[data-locator-filters-hint]');
                if (filters) {
                    filters.classList.remove('is-locked');
                    filters.removeAttribute('aria-disabled');
                    filters.querySelectorAll('.s-locator__toggle[disabled]').forEach((btn) => {
                        btn.removeAttribute('disabled');
                    });
                }
                if (hint) hint.hidden = true;

                // On mobile/narrow viewports the map sits below the search
                // panel — auto-scroll to it after a selection so users see
                // their result pins without having to scroll manually.
                const mobile = window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;
                if (mobile && els.mapEl) {
                    // Small delay so layout settles after the map re-centres.
                    setTimeout(() => {
                        els.mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 120);
                }

                // Map was already initialised on page load — make sure it's
                // ready (handles the edge case where the script is still
                // loading when the user picks a suggestion quickly).
                if (config.apiKey) {
                    loadGoogleMaps(config)
                        .then(() => { if (!state.map) initMap(state, els, config); })
                        .then(() => maybeRunSearch())
                        .catch(() => maybeRunSearch());
                } else {
                    maybeRunSearch();
                }
            });
        }

        // ─── Dropdown filters (checkbox multi-select + radio single-select) ──
        root.querySelectorAll('[data-locator-dropdown]').forEach((dd) => {
            const btn = dd.querySelector('.s-locator__toggle');
            const menu = dd.querySelector('.s-locator__menu');
            const summary = dd.querySelector('[data-locator-summary]');
            if (!btn || !menu) return;

            const facet = dd.getAttribute('data-locator-dropdown');
            const isSingle = dd.classList.contains('s-locator__dropdown--single');

            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const open = dd.classList.toggle('is-open');
                btn.setAttribute('aria-expanded', open ? 'true' : 'false');
                if (open) closeOtherDropdowns(dd);
            });
            menu.addEventListener('click', (e) => e.stopPropagation());

            if (isSingle) {
                // Radio — single value, closes on select.
                dd.querySelectorAll('input[type="radio"][data-locator-radio]').forEach((rb) => {
                    rb.addEventListener('change', () => {
                        updateSingleSummary(dd, summary);
                        if (facet === 'radius') {
                            state.radius = Math.max(1, Math.min(100, parseInt(rb.value, 10) || 100));
                        } else {
                            state[facet] = rb.value;
                        }
                        dd.classList.remove('is-open');
                        btn.setAttribute('aria-expanded', 'false');
                        if (state.lat && state.lng) maybeRunSearch();
                    });
                });
                updateSingleSummary(dd, summary);
            } else {
                // Checkbox — multi-select, stays open.
                dd.querySelectorAll('input[type="checkbox"][data-locator-checkbox]').forEach((cb) => {
                    cb.addEventListener('change', () => {
                        updateSummary(dd, summary, config.i18n);
                        state[facet] = collectCheckedFromContainer(dd);
                        if (state.lat && state.lng) maybeRunSearch();
                    });
                });
                updateSummary(dd, summary, config.i18n);
            }
        });

        // Click outside closes all dropdowns.
        document.addEventListener('click', () => closeOtherDropdowns(null));

        // ─── Prevent form submit ──────────────────────────────────────────
        if (els.form) els.form.addEventListener('submit', (e) => e.preventDefault());

        // ─── Modal close handlers ─────────────────────────────────────────
        root.querySelectorAll('[data-locator-modal-close]').forEach((el) => {
            el.addEventListener('click', () => closeModal(els.modal));
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') closeModal(els.modal);
        });

        // ─── Card click delegation ────────────────────────────────────────
        els.items.addEventListener('click', (e) => {
            const card = e.target.closest('[data-locator-card]');
            if (!card) return;
            openModalFromCard(card, els);
        });

        function maybeRunSearch() {
            if (state.searchPending) return;
            state.searchPending = true;
            runSearch(state, els, config).finally(() => {
                state.searchPending = false;
            });
        }

        // Keep the scroll-lock attribute honest on resize.
        updateScrollLock(els);
        window.addEventListener('resize', () => updateScrollLock(els));
    }

    // ═══════════════════════════════════════════════════════════════════════
    // Postcode autocomplete (reads wp_lea_au_postcodes)
    // ═══════════════════════════════════════════════════════════════════════
    class PostcodeAutocomplete {
        constructor(input, listEl, config, onSelect) {
            this.input = input;
            this.list = listEl;
            this.config = config;
            this.onSelect = onSelect;
            this.timer = null;
            this.rows = [];
            this.highlightIndex = -1;
            this.wire();
        }

        wire() {
            this.input.addEventListener('input', (e) => {
                const q = e.target.value.trim();
                clearTimeout(this.timer);
                this.input.dataset.selected = '';
                if (q.length < 2) { this.close(); return; }
                this.timer = setTimeout(() => this.query(q), 250);
            });

            this.input.addEventListener('keydown', (e) => {
                if (!this.rows.length && e.key !== 'Escape') return;
                if (e.key === 'ArrowDown') { e.preventDefault(); this.move(1); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); this.move(-1); }
                else if (e.key === 'Enter') {
                    if (this.highlightIndex >= 0 && this.rows[this.highlightIndex]) {
                        e.preventDefault();
                        this.select(this.rows[this.highlightIndex]);
                    }
                } else if (e.key === 'Escape') {
                    this.close();
                }
            });

            document.addEventListener('click', (e) => {
                if (e.target !== this.input && !this.list.contains(e.target)) {
                    this.close();
                }
            });
        }

        async query(q) {
            const url = new URL(this.config.ajaxUrl, window.location.origin);
            url.searchParams.set('action', 'lea_locator_search_postcodes');
            url.searchParams.set('nonce', this.config.nonce);
            url.searchParams.set('query', q);
            try {
                const res = await fetch(url.toString(), { credentials: 'same-origin' });
                const json = await res.json();
                if (!json.success) { this.close(); return; }
                this.render(json.data || []);
            } catch (e) {
                this.close();
            }
        }

        render(rows) {
            this.rows = rows;
            this.highlightIndex = -1;
            if (!rows.length) {
                this.list.innerHTML = '<div class="s-locator__suggestion s-locator__suggestion--empty">' +
                    escapeHtml(this.config.i18n.noSuggestions || 'No matching suburbs or postcodes') + '</div>';
                this.open();
                return;
            }
            this.list.innerHTML = rows.map((r, i) =>
                '<button type="button" class="s-locator__suggestion" data-i="' + i + '">' +
                '<span class="s-locator__suggestion-locality">' + escapeHtml(r.locality) + '</span>' +
                '<span class="s-locator__suggestion-meta">' + escapeHtml(r.state) + ' ' + escapeHtml(r.postcode) + '</span>' +
                '</button>'
            ).join('');
            this.list.querySelectorAll('[data-i]').forEach((btn) => {
                btn.addEventListener('mousedown', (e) => e.preventDefault()); // prevent blur
                btn.addEventListener('click', () => {
                    this.select(this.rows[parseInt(btn.getAttribute('data-i'), 10)]);
                });
            });
            this.open();
        }

        move(delta) {
            if (!this.rows.length) return;
            this.highlightIndex = (this.highlightIndex + delta + this.rows.length) % this.rows.length;
            const buttons = this.list.querySelectorAll('[data-i]');
            buttons.forEach((b, i) => {
                b.classList.toggle('is-active', i === this.highlightIndex);
                if (i === this.highlightIndex) b.scrollIntoView({ block: 'nearest' });
            });
        }

        select(row) {
            if (!row) return;
            this.input.value = row.label;
            this.input.dataset.selected = '1';
            this.close();
            if (typeof this.onSelect === 'function') this.onSelect(row);
        }

        open()  { this.list.classList.add('is-open'); }
        close() { this.list.classList.remove('is-open'); this.list.innerHTML = ''; this.rows = []; this.highlightIndex = -1; }
    }

    // ═══════════════════════════════════════════════════════════════════════
    // Google Maps lazy loader
    // ═══════════════════════════════════════════════════════════════════════
    function loadGoogleMaps(config) {
        if (window.google && window.google.maps) return Promise.resolve();
        if (mapsReadyPromise) return mapsReadyPromise;

        window.gm_authFailure = function () {
            reportMapError(config, 'AUTH_FAILURE', 'Google Maps auth failed. Check API key, billing, and HTTP referrer restrictions.');
        };

        mapsReadyPromise = new Promise((resolve, reject) => {
            if (mapsScriptInjected) {
                const start = Date.now();
                const iv = setInterval(() => {
                    if (window.google && window.google.maps) {
                        clearInterval(iv); resolve();
                    } else if (Date.now() - start > 15000) {
                        clearInterval(iv); reject(new Error('timeout'));
                    }
                }, 120);
                return;
            }
            mapsScriptInjected = true;

            window.__leaLocatorMapsReady = function () {
                resolve(); delete window.__leaLocatorMapsReady;
            };

            const s = document.createElement('script');
            s.src = 'https://maps.googleapis.com/maps/api/js?key=' +
                    encodeURIComponent(config.apiKey) +
                    '&callback=__leaLocatorMapsReady&v=weekly&loading=async';
            s.async = true; s.defer = true;
            s.onerror = () => reject(new Error('Script load error'));
            document.head.appendChild(s);
        });
        return mapsReadyPromise;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // Map init + markers
    // ═══════════════════════════════════════════════════════════════════════
    function initMap(state, els, config) {
        if (!els.mapEl || state.map) return;
        const center = config.auCenter || { lat: -25.2744, lng: 133.7751, zoom: 4 };
        // Mobile viewport shows roughly half the horizontal span at zoom 4 —
        // step down one level so the whole of Australia is visible by default.
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches;
        const initialZoom = isMobile ? Math.max(2, (center.zoom || 4) - 1) : (center.zoom || 4);
        try {
            state.map = new google.maps.Map(els.mapEl, {
                center: { lat: center.lat, lng: center.lng },
                zoom:   initialZoom,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                // Cooperative = user must hold Ctrl (⌘ on mac) to zoom with
                // the wheel, so plain scrolling passes through to the page.
                // Shows a short overlay hint the first time. This also stops
                // one/two-finger scroll-jacks on touchpads.
                gestureHandling: 'cooperative',
                styles: config.mapStyles || [],
            });
        } catch (e) {
            reportMapError(config, 'INIT_FAILURE', String(e.message || e));
        }
    }

    async function runSearch(state, els, config) {
        if (!state.lat || !state.lng) return;

        els.items.innerHTML = '<p class="s-locator__placeholder">' + escapeHtml(config.i18n.searching) + '</p>';
        els.count.textContent = '';

        const body = new URLSearchParams();
        body.set('action', 'lea_locator_search');
        body.set('nonce', config.nonce);
        body.set('lat', String(state.lat));
        body.set('lng', String(state.lng));
        body.set('radius', String(state.radius));
        body.set('locality', state.suburb);
        body.set('postcode', state.postcode);
        body.set('state', state.stateAbbr);
        // Only the first search after a fresh suburb selection gets logged.
        if (state.newSelection) body.set('log', '1');
        state.newSelection = false;
        (state.regions || []).forEach(v => body.append('regions[]', v));
        (state.types   || []).forEach(v => body.append('types[]',   v));

        try {
            const res = await fetch(config.ajaxUrl, {
                method: 'POST', body,
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                credentials: 'same-origin',
            });
            const json = await res.json();
            if (!json.success) throw new Error(typeof json.data === 'string' ? json.data : 'Search failed');

            if (json.data.disabled) {
                els.items.innerHTML = '<p class="s-locator__placeholder">' + escapeHtml(config.i18n.noResults) + '</p>';
                els.list.classList.remove('has-results');
                updateScrollLock(els);
                return;
            }

            if (json.data.count === 0) {
                els.items.innerHTML = '<p class="s-locator__placeholder">' + escapeHtml(config.i18n.noResults) + '</p>';
                els.count.textContent = '';
                els.list.classList.remove('has-results');
            } else {
                els.items.innerHTML = json.data.html;
                const tmpl = json.data.count === 1 ? config.i18n.countOne : config.i18n.countMany;
                els.count.textContent = tmpl.replace('%d', json.data.count).replace('%d', state.radius);
                els.list.classList.add('has-results');
            }

            renderMarkers(state, els, json.data.items);
            updateScrollLock(els);
        } catch (e) {
            els.items.innerHTML = '<p class="s-locator__placeholder s-locator__placeholder--error">Error: ' + escapeHtml(e.message) + '</p>';
        }
    }

    function renderMarkers(state, els, items) {
        if (!state.map || !window.google) return;
        const gm = google.maps;

        function markerUrlFor(type) {
            const map = state.markerMap || {};
            const key = String(type || '').trim().toLowerCase();
            return map[key] || map['default'] || window.__leaLocatorMarkerUrl || '';
        }

        state.markers.forEach((m) => m.setMap(null));
        state.markers = [];
        state.markersByCardId = {};
        if (state.userMarker) { state.userMarker.setMap(null); state.userMarker = null; }

        if (state.lat && state.lng) {
            state.userMarker = new gm.Marker({
                map: state.map,
                position: { lat: state.lat, lng: state.lng },
                icon: {
                    path: gm.SymbolPath.CIRCLE,
                    fillColor: '#F28C28', fillOpacity: 1,
                    strokeColor: '#ffffff', strokeWeight: 3, scale: 8,
                },
                title: 'Your location', zIndex: 1000,
            });
        }

        const bounds = new gm.LatLngBounds();
        if (state.lat && state.lng) bounds.extend({ lat: state.lat, lng: state.lng });

        items.forEach((item) => {
            if (!item.lat || !item.lng) return;
            const marker = new gm.Marker({
                map: state.map,
                position: { lat: item.lat, lng: item.lng },
                title: item.title,
                icon: {
                    url: markerUrlFor(item.primary_type),
                    // SVG viewBox is 32.55 × 46 (aspect ~1:1.413). Previous
                    // 30×40 was squashed vertically which cropped the top of
                    // the dome. Matching the aspect here keeps the full pin
                    // visible; anchor Y equals the scaled height so the tip
                    // sits on the actual coordinate.
                    scaledSize: new gm.Size(28, 40),
                    anchor: new gm.Point(14, 40),
                },
            });
            // Clicking a marker opens the modal (same as clicking the card).
            marker.addListener('click', () => {
                const card = els.items.querySelector('[data-locator-card][data-id="' + item.id + '"]');
                if (card) {
                    openModalFromCard(card, els);
                    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            });
            state.markers.push(marker);
            state.markersByCardId[item.id] = marker;
            bounds.extend({ lat: item.lat, lng: item.lng });
        });

        if (items.length) {
            state.map.fitBounds(bounds, 60);
            gm.event.addListenerOnce(state.map, 'bounds_changed', () => {
                if (state.map.getZoom() > 13) state.map.setZoom(13);
            });
        }
    }

    // ═══════════════════════════════════════════════════════════════════════
    // Modal
    // ═══════════════════════════════════════════════════════════════════════
    function openModalFromCard(card, els) {
        const lat   = parseFloat(card.getAttribute('data-lat'));
        const lng   = parseFloat(card.getAttribute('data-lng'));
        const title = card.getAttribute('data-title') || '';
        const addr  = card.getAttribute('data-address') || '';
        const phone = card.getAttribute('data-phone') || '';
        const email = card.getAttribute('data-email') || '';
        const site  = card.getAttribute('data-website') || '';

        els.modalTitle.textContent = title;
        els.modalAddr.textContent  = addr;

        // Region + type pills.
        if (els.modalPills) {
            const regions = (card.getAttribute('data-regions') || '').split('|').filter(Boolean);
            const types   = (card.getAttribute('data-types')   || '').split('|').filter(Boolean);
            els.modalPills.innerHTML = [
                ...regions.map(n => '<span class="s-locator__pill s-locator__pill--region">' + escapeHtml(n) + '</span>'),
                ...types.map(n   => '<span class="s-locator__pill s-locator__pill--type">'   + escapeHtml(n) + '</span>'),
            ].join('');
        }

        setContactRow(els.modalPhoneWrap, els.modalPhone, phone, phone ? 'tel:' + phone.replace(/\s+/g, '') : null);
        setContactRow(els.modalEmailWrap, els.modalEmail, email, email ? 'mailto:' + email : null);
        setContactRow(els.modalSiteWrap,  els.modalSite,  site,  site);

        els.modalDirs.href = (lat && lng)
            ? 'https://www.google.com/maps/dir/?api=1&destination=' + lat + ',' + lng
            : '#';

        els.modal.hidden = false;
        document.body.classList.add('is-locator-modal-open');
    }

    function setContactRow(wrap, link, text, href) {
        if (!text) { if (wrap) wrap.hidden = true; return; }
        if (wrap) wrap.hidden = false;
        link.textContent = text;
        if (href) link.setAttribute('href', href);
    }

    function closeModal(modal) {
        if (!modal) return;
        modal.hidden = true;
        document.body.classList.remove('is-locator-modal-open');
    }

    // ═══════════════════════════════════════════════════════════════════════
    // Helpers
    // ═══════════════════════════════════════════════════════════════════════
    function collectCheckedFromContainer(container) {
        if (!container) return [];
        return Array.from(container.querySelectorAll('input[type="checkbox"][data-locator-checkbox]:checked'))
            .map(c => c.value);
    }

    function updateSingleSummary(dd, summary) {
        const checked = dd.querySelector('input[type="radio"][data-locator-radio]:checked');
        if (!summary || !checked) return;
        const label = checked.parentElement.querySelector('.s-locator__option-label');
        summary.textContent = label ? label.textContent : checked.value;
    }

    function readRadiusFromDropdown(root) {
        const dd = root.querySelector('[data-locator-dropdown="radius"]');
        if (!dd) return null;
        const checked = dd.querySelector('input[type="radio"][data-locator-radio]:checked');
        return checked ? parseInt(checked.value, 10) : null;
    }

    function updateSummary(dd, summary, i18n) {
        const boxes = dd.querySelectorAll('input[type="checkbox"][data-locator-checkbox]');
        const checked = Array.from(boxes).filter(b => b.checked);
        if (!summary) return;
        if (checked.length === boxes.length || checked.length === 0) {
            summary.textContent = i18n.all || 'All';
        } else if (checked.length === 1) {
            const label = checked[0].parentElement.querySelector('.s-locator__option-label');
            summary.textContent = label ? label.textContent : '1 selected';
        } else {
            summary.textContent = (i18n.nSelected || '%d selected').replace('%d', checked.length);
        }
    }

    function closeOtherDropdowns(except) {
        document.querySelectorAll('.s-locator__dropdown.is-open').forEach((d) => {
            if (d === except) return;
            d.classList.remove('is-open');
            const btn = d.querySelector('.s-locator__toggle');
            if (btn) btn.setAttribute('aria-expanded', 'false');
        });
    }

    /**
     * Toggle `data-lenis-prevent` on the scroll container only when it
     * actually overflows. Lenis walks up the DOM from the cursor and blocks
     * its smooth-scroll hijack when it hits this attr — we only want that
     * behaviour while the user has something to scroll through.
     */
    function updateScrollLock(els) {
        if (!els.scroll) return;
        const overflows = els.scroll.scrollHeight > els.scroll.clientHeight + 1;
        if (overflows) els.scroll.setAttribute('data-lenis-prevent', '');
        else els.scroll.removeAttribute('data-lenis-prevent');
    }

    function showMapUnavailable(mapEl, msg) {
        if (!mapEl) return;
        mapEl.innerHTML = '<p class="s-locator__map-empty">' + escapeHtml(msg) + '</p>';
    }

    function reportMapError(config, code, message) {
        const body = new URLSearchParams();
        body.set('action', 'lea_locator_map_error');
        body.set('nonce', config.nonce);
        body.set('error_code', code);
        body.set('message', message);
        body.set('url', window.location.href);
        body.set('referer', document.referrer || '');
        fetch(config.ajaxUrl, { method: 'POST', body, credentials: 'same-origin' }).catch(() => {});
    }

    function escapeHtml(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    // Stash marker URL globally so every instance shares it.
    document.addEventListener('DOMContentLoaded', () => {
        const firstConfig = document.querySelector('[data-locator-config]');
        if (!firstConfig || window.__leaLocatorMarkerUrl) return;
        try {
            const c = JSON.parse(firstConfig.textContent);
            if (c && c.markerUrl) window.__leaLocatorMarkerUrl = c.markerUrl;
        } catch (e) { /* ignore */ }
    });

})();
